import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, useTheme } from '@mui/material';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteTablePagination from '../InfiniteTablePagination';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useSearchContactsInfiniteQuery } from '@/features/contacts/contactsApi';
import { setBrokerGroupId } from '@/features/table/clientContactsPrefsSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { chunk } from 'lodash';
import { getComparator, stableSort } from '@/components/table/functions';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import { CLIENT_PAGE_SIZE, columns as dataColumns, DEFAULT_HIDDEN_COLUMNS } from './data';
import InsightContactsTableFilters from './InsightContactsTableFilters';
import { getDealClientId } from '@/features/deals/helper';

const InsightContactsTable = () => (
	<ColumnControlContextProvider
		variant='insightContacts'
		value='list'
		defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
	>
		<InsightContactsTableContent />
	</ColumnControlContextProvider>
);

const InsightContactsTableContent = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const filters = useSelector((state) => state.clientContactsPrefs);
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [searchText, setSearchText] = useState('');
	const [searchValue, setSearchValue] = useState('');
	const [refreshIndicator, setRefreshIndicator] = useState(0);
	const [paginationModel, setPaginationModel] = useState({
		pageSize: CLIENT_PAGE_SIZE,
		page: 0,
	});
	const [sortModel, setSortModel] = useState([{ field: 'Addressee', sort: 'asc' }]);

	const {
		columns: columnsWithWidths,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(dataColumns);

	useEffect(() => {
		if (!filters?.brokerGroupId && userDetails?.brokerGroupId) {
			dispatch(setBrokerGroupId(userDetails.brokerGroupId));
		}
	}, [dispatch, filters?.brokerGroupId, userDetails?.brokerGroupId]);

	const { data, fetchNextPage, hasNextPage, ...infiniteQuery } = useSearchContactsInfiniteQuery(
		{
			...(searchValue && { searchText: searchValue }),
			...(filters?.ownerIds?.length > 0 && { ownerDocIds: filters.ownerIds }),
			...(filters?.brokerGroupId && { brokerGroupId: filters.brokerGroupId }),
			...(filters?.isPrimary === 'yes' && { isPrimary: true }),
			...(filters?.isPrimary === 'no' && { isPrimary: false }),
		},
		{ skip: !userDetails || userQuery.isFetching },
	);

	const allPageData = useMemo(() => {
		return (data?.pages ?? []).flatMap((page) =>
			(page?.Results ?? []).map((contact) => ({
				...contact,
				id: contact.id ?? contact.Id,
			})),
		);
	}, [data?.pages]);

	// Sort/filter client-side across currently loaded Insight pages (not a remote Insight sort).
	const allPages = stableSort(
		allPageData,
		getComparator(sortModel[0].sort, sortModel[0].field, true),
	);

	const pageChunks = chunk(allPages ?? [], paginationModel.pageSize);
	const totalRecordCount = data?.pages?.[0]?.RecordCount;
	const hasFolioFilters =
		filters.ownerIds?.length > 0 ||
		Boolean(filters.brokerGroupId) ||
		filters.isPrimary !== 'all';
	const count = useMemo(() => {
		// Folio-side filters shrink Results vs Insight RecordCount — use loaded rows
		if (hasFolioFilters) return allPages.length;
		if (totalRecordCount != null && hasNextPage) return totalRecordCount;
		return allPageData.length;
	}, [hasFolioFilters, allPages.length, allPageData.length, totalRecordCount, hasNextPage]);

	const displayedRows = useMemo(() => {
		if (!pageChunks.length) return [];
		return pageChunks[paginationModel.page] || [];
	}, [pageChunks, paginationModel.page]);

	useEffect(() => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	}, [searchValue, filters.ownerIds, filters.brokerGroupId, filters.isPrimary]);

	// Load more Insight pages only when the current client page needs more rows
	useEffect(() => {
		const neededRows = (paginationModel.page + 1) * paginationModel.pageSize;
		if (
			infiniteQuery.isUninitialized ||
			infiniteQuery.isFetching ||
			!hasNextPage ||
			allPageData.length >= neededRows
		) {
			return;
		}
		fetchNextPage();
	}, [
		paginationModel.page,
		paginationModel.pageSize,
		allPageData.length,
		hasNextPage,
		infiniteQuery.isFetching,
		infiniteQuery.isUninitialized,
		fetchNextPage,
	]);

	const isInitialLoading =
		infiniteQuery.isLoading || (infiniteQuery.isFetching && !data?.pages?.length);

	useEffect(() => {
		if (!infiniteQuery.isUninitialized && data) {
			infiniteQuery.refetch();
			if (paginationModel.page != 0) {
				setPaginationModel((prev) => ({ ...prev, page: 0 }));
			}
		}
	}, [refreshIndicator]);

	const handleRowClick = useCallback(
		({ row }, event) => {
			if (
				event?.target?.closest?.(
					'button, a[href], .MuiLink-root, .has-link, .MuiCheckbox-root, input, [role="button"]',
				)
			) {
				return;
			}
			const clientId = getDealClientId(row);
			if (!clientId || !row?.Id) return;
			dispatch(
				openDialog({
					dialogName: 'editContact',
					data: {
						clientId,
						contactId: row.Id,
					},
				}),
			);
		},
		[dispatch],
	);

	const handlePaginationModelChange = (newModel) => {
		setPaginationModel((prev) => ({ ...prev, page: newModel?.page }));
	};

	const Toolbar = useCallback(
		() => (
			<GridToolbarContainer>
				<GridToolbarColumnsButton />
				<Box sx={{ flexGrow: 1 }} />
				<IconButton size='small' onClick={() => setRefreshIndicator((prev) => prev + 1)}>
					<RefreshIcon sx={{ fontSize: '1.25rem', color: theme.palette.primary.main }} />
				</IconButton>
			</GridToolbarContainer>
		),
		[theme],
	);

	const TablePagination = useCallback(() => {
		const totalClientPages = Math.ceil(allPages.length / paginationModel.pageSize);
		return (
			<InfiniteTablePagination
				count={count}
				page={paginationModel.page}
				setPage={(page) => handlePaginationModelChange({ page })}
				rowsPerPage={paginationModel.pageSize}
				hasNextPage={paginationModel.page + 1 < totalClientPages}
				hasPreviousPage={paginationModel.page > 0}
			/>
		);
	}, [count, allPages, paginationModel.pageSize, paginationModel.page]);

	return (
		<Stack sx={{ width: '100%' }} spacing={2}>
			<InsightContactsTableFilters
				isInitialLoading={isInitialLoading}
				searchParams={{
					searchText,
					setSearchText,
					searchValue,
					setSearchValue,
					setPaginationModel,
					disabled: isInitialLoading,
				}}
			/>
			<Box
				className='MuiDataGrid-parentDiv'
				sx={{
					display: 'flex',
					flexDirection: 'column',
					minHeight: '8em',
					maxHeight: 'calc(120vh - 300px)',
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					overflow: 'hidden',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<DataGrid
					loading={isInitialLoading}
					width='100%'
					getRowHeight={() => 'auto'}
					getRowId={(row) => String(row?.id)}
					rows={displayedRows}
					columns={columnsWithWidths}
					onRowClick={handleRowClick}
					disableRowSelectionOnClick
					disableDensitySelector
					disableColumnMenu
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={onColumnVisibilityModelChange}
					onColumnWidthChange={onColumnWidthChange}
					sx={{
						backgroundColor: theme.palette.background.paper,
						border: 'none',
						borderRadius: '8px',
						'& .MuiDataGrid-row': {
							cursor: 'pointer',
						},
						'& .MuiDataGrid-cell': {
							padding: 1,
							minHeight: '42px',
						},
						'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
							outline: 'none',
						},
						'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
							{
								outline: 'none',
							},
						minHeight: '6em',
						height: isInitialLoading ? '6em' : 'auto',
					}}
					slots={{
						noRowsOverlay: infiniteQuery.isError
							? () => <NoRowsErrorOverlay objectType='contacts' />
							: () => <NoRowsOverlay objectType='contacts' />,
						noResultsOverlay: infiniteQuery.isError
							? () => <NoRowsErrorOverlay objectType='contacts' />
							: () => <NoRowsOverlay objectType='contacts' />,
						toolbar: Toolbar,
						pagination: TablePagination,
					}}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
					}}
					paginationMode='server'
					filterMode='server'
					sortingMode='server'
					rowCount={count}
					paginationModel={paginationModel}
					sortModel={sortModel}
					onSortModelChange={(newModel) => {
						setPaginationModel((prev) => ({ ...prev, page: 0 }));
						setSortModel(newModel);
					}}
				/>
			</Box>
		</Stack>
	);
};

export default InsightContactsTable;
